// src/authpages/profile/Profile.js
import React, { useState, useEffect } from 'react';
import { Edit, User, Mail, Briefcase, Calendar, Save, X, Building2, GraduationCap, BookOpen, Clock, Book } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';

// ProfileSection component for displaying and editing individual profile fields
const ProfileSection = ({ icon, title, value, isEditing, onEditChange, fieldName, editable = false }) => {
  const isFieldEditable = editable && isEditing;
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 flex items-center space-x-4">
      <div className="text-blue-600 opacity-70">
        {icon}
      </div>
      <div className="flex-grow">
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        {isFieldEditable ? (
          <input
            type="text"
            className="w-full text-lg font-semibold text-gray-800 border-b border-blue-500 focus:outline-none"
            value={value || ''}
            onChange={(e) => onEditChange(fieldName, e.target.value)}
          />
        ) : (
          <p className="text-lg font-semibold text-gray-800">{value || 'Not provided'}</p>
        )}
      </div>
    </div>
  );
};

const Profile = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [editedUserInfo, setEditedUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Simulate getting the current user ID (replace with your auth logic)
  const getCurrentUserId = () => {
    // This should be replaced with your actual authentication logic
    return localStorage.getItem('userId') || 'testUser123';
  };

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
    
    const fetchUserData = async (user) => {
      try {
        setLoading(true);
        
        // Get user document from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const firestoreUserData = userDocSnap.data();
          
          // Combine Firebase auth user data with Firestore data
          const userData = {
            uid: user.uid,
            email: firestoreUserData.email || user.email,
            fullName: firestoreUserData.fullName || user.displayName || 'Not provided',
            degreeTitle: firestoreUserData.degreeTitle || 'Not specified',
            createdAt: firestoreUserData.createdAt || user.metadata.creationTime,
            institution: firestoreUserData.institution || 'Not provided',
            dateOfBirth: firestoreUserData.dateOfBirth || null,
            areaOfStudy: firestoreUserData.areaOfStudy || 'Not provided',
            educationLevel: firestoreUserData.educationLevel || 'Not provided',
            studyYear: firestoreUserData.studyYear || 'Not provided'
          };
          
          setUserInfo(userData);
          setEditedUserInfo(userData);
        } else {
          // No user document found - create one with basic info
          console.log("No user document found. Creating a basic profile.");
          const basicUserData = {
            uid: user.uid,
            email: user.email,
            fullName: user.displayName || 'New User',
            createdAt: user.metadata.creationTime,
            degreeTitle: 'Not specified',
            institution: 'Not provided',
            dateOfBirth: null,
            areaOfStudy: 'Not provided',
            educationLevel: 'Not provided',
            studyYear: 'Not provided'
          };
          
          setUserInfo(basicUserData);
          setEditedUserInfo(basicUserData);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(`Error fetching user data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserData(user);
      } else {
        setError('No user signed in');
        setLoading(false);
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const handleEditChange = (field, value) => {
    setEditedUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!editedUserInfo) return;
    
    setIsSaving(true);
    try {
      const db = getFirestore();
      const userDocRef = doc(db, 'users', editedUserInfo.uid);
      
      // Only update editable fields
      await updateDoc(userDocRef, {
        fullName: editedUserInfo.fullName,
        degreeTitle: editedUserInfo.degreeTitle
      });
      
      setUserInfo(editedUserInfo);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(`Failed to update profile: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedUserInfo(userInfo);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="text-gray-500">Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Profile Information</h1>
        {isEditing ? (
          <div className="flex space-x-4">
            <button 
              onClick={handleCancelEdit}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              disabled={isSaving}
            >
              <X size={20} className="mr-2" />
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="flex items-center text-green-600 hover:text-green-800 transition-colors"
              disabled={isSaving}
            >
              <Save size={20} className="mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : (
          <button 
            onClick={() => setIsEditing(true)}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <Edit size={20} className="mr-2" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Tabs for basic information only */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className="py-2 px-4 font-medium text-blue-600 border-b-2 border-blue-600"
        >
          Basic Information
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ProfileSection 
          icon={<User size={24} />}
          title="Full Name"
          value={isEditing ? editedUserInfo.fullName : userInfo.fullName}
          isEditing={isEditing}
          onEditChange={handleEditChange}
          fieldName="fullName"
          editable={true}
        />
        <ProfileSection 
          icon={<Mail size={24} />}
          title="Email Address"
          value={userInfo.email}
          isEditing={isEditing}
          onEditChange={handleEditChange}
          fieldName="email"
        />
        <ProfileSection 
          icon={<GraduationCap size={24} />}
          title="Degree Title"
          value={isEditing ? editedUserInfo.degreeTitle : userInfo.degreeTitle}
          isEditing={isEditing}
          onEditChange={handleEditChange}
          fieldName="degreeTitle"
          editable={true}
        />
        <ProfileSection 
          icon={<Calendar size={24} />}
          title="Account Created"
          value={userInfo.createdAt 
            ? new Date(userInfo.createdAt).toLocaleDateString() 
            : 'Not available'}
          isEditing={isEditing}
          onEditChange={handleEditChange}
          fieldName="createdAt"
        />
      </div>
    </div>
  );
};

export default Profile;